import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core'
import { RecoveryPointDto } from 'src/app/services/dtos'
import { equalSets, shortTimeString, timeAgoString } from '../utils'

export interface RpSliderChangedEvent {
  recoveryPointDto: RecoveryPointDto
  isRealTime: boolean
}

// User intent — held independently of recoveryPoints' shape so toggling apps
// upstream (which mutates the union) doesn't drag the thumb. The DOM `<input
// type=range>` value is a derived projection of intent on a fixed time axis,
// which keeps the thumb's pixel position stable across selection changes.
// 'latest' is the default sticky intent: it always resolves to the newest RP
// in the current list, so a refresh that brings in a newer RP advances the
// selection automatically (rather than pinning to the now-stale prior latest).
type SliderIntent =
  | { kind: 'latest' }
  | { kind: 'realTime' }
  | { kind: 'rpId'; id: string; anchorTs: number }
  | { kind: 'time'; ts: number }

function rpSortFunction(l: RecoveryPointDto, r: RecoveryPointDto) {
  return l.timestamp.valueOf() - r.timestamp.valueOf()
}

// Fixed-resolution track. The `<input>` always spans 0..TRACK_MAX regardless
// of how many RPs are in the union — pixel position derives from a timestamp
// ratio, not an index, so removing an interior RP doesn't shift the thumb.
const TRACK_MAX = 1000
// Right-edge zone reserved for the "Now" snap when real-time is enabled.
const REALTIME_GAP = 50

@Component({
  selector: 'rp-range-slider',
  templateUrl: './rp-range-slider.component.html',
  styleUrls: ['./rp-range-slider.component.scss'],
  standalone: false,
})
export class RpRangeSliderComponent implements OnInit {
  @ViewChild('rangeControl', { static: true }) rangeControl: ElementRef
  @Input() recoveryPoints: Array<RecoveryPointDto> = null
  @Input() enableRealTimeRecovery: boolean = null
  @Output() update = new EventEmitter<RpSliderChangedEvent>()

  private _intent: SliderIntent | null = null
  // Last RP id / real-time flag we actually emitted; lets us short-circuit when
  // recoveryPoints churns by reference but the resolved RP didn't change,
  // avoiding CD-cycle re-emit cascades.
  private _lastEmittedRpId: string | null = null
  private _lastEmittedRealTime: boolean | null = null

  constructor() {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if ('recoveryPoints' in changes) {
      // Reference-only churn (parent's getter rebuilt the array but contents
      // are identical) is a no-op; without this guard the slider re-derives
      // every CD pass.
      let hasChanges = true
      if (!!changes.recoveryPoints.previousValue) {
        const prev = changes.recoveryPoints.previousValue as Array<RecoveryPointDto>
        if (!!prev) {
          const prevSet = new Set(prev.map((rp) => rp.recoveryPointId))
          const curr = changes.recoveryPoints.currentValue as Array<RecoveryPointDto>
          const currSet = new Set(curr.map((rp) => rp.recoveryPointId))
          if (equalSets(prevSet, currSet)) {
            hasChanges = false
          }
        }
      }

      if (hasChanges) {
        // First mount: when any selected app supports realtime, default to "Now"
        // (matches pre-redesign behavior); otherwise the sticky 'latest' intent
        // tracks new RPs added during refresh.
        if (!changes.recoveryPoints.previousValue) {
          this._intent = this.enableRealTimeRecovery ? { kind: 'realTime' } : { kind: 'latest' }
        }

        // Async emit avoids "NG0100: Expression has changed after it was
        // checked" when the parent updates state in response to (update).
        setTimeout(() => this.announceFromIntent(), 0)
      }
    }
  }

  // Track-bound projection of intent. Read by [value] every CD pass, which
  // keeps the thumb pinned to the user's RP regardless of how the underlying
  // recoveryPoints array mutates upstream.
  get value(): string {
    return `${this.resolveIntentToTrackValue()}`
  }

  get trackMax(): number {
    return TRACK_MAX
  }

  // Resolution priority:
  //   latest → rightmost RP position (always tracks the newest in the list)
  //   realTime → TRACK_MAX (when still supported)
  //   rpId still present → its timestamp's track position
  //   rpId gone → degrade to time intent and fall through
  //   time → newest RP with timestamp ≤ anchor (matches the "as-of" semantics
  //         used in computeApplicationRecoveryPoint)
  //   anchor predates everything → oldest survivor (preserves "go back in
  //         time" intent over a jarring snap to latest)
  private resolveIntentToTrackValue(): number {
    if (!this.recoveryPoints || this.recoveryPoints.length === 0) return 0

    if (!this._intent || this._intent.kind === 'latest') {
      return this.tsToTrackValue(this.tMax)
    }

    if (this._intent.kind === 'realTime') {
      if (this.enableRealTimeRecovery) return TRACK_MAX
      // Real-time no longer supported — fall back to latest RP.
      return this.tsToTrackValue(this.tMax)
    }

    const sorted = this.sortedRecoveryPoints

    if (this._intent.kind === 'rpId') {
      const { id, anchorTs } = this._intent
      const rp = sorted.find((r) => r.recoveryPointId === id)
      if (rp) return this.tsToTrackValue(rp.timestamp.valueOf())
      this._intent = { kind: 'time', ts: anchorTs }
    }

    if (this._intent.kind === 'time') {
      const ts = this._intent.ts
      for (let i = sorted.length - 1; i >= 0; i--) {
        if (sorted[i].timestamp.valueOf() <= ts) return this.tsToTrackValue(sorted[i].timestamp.valueOf())
      }
      return this.tsToTrackValue(sorted[0].timestamp.valueOf())
    }

    return TRACK_MAX
  }

  // Map an RP's timestamp to a position on the track. RPs occupy [0..rpAxisMax];
  // when realtime is enabled the last REALTIME_GAP px is reserved for "Now".
  private tsToTrackValue(ts: number): number {
    const tMin = this.tMin
    const tMax = this.tMax
    if (tMax === tMin) return this.rpAxisMax
    return Math.round(((ts - tMin) / (tMax - tMin)) * this.rpAxisMax)
  }

  private get tMin(): number {
    return this.sortedRecoveryPoints[0].timestamp.valueOf()
  }

  private get tMax(): number {
    return this.sortedRecoveryPoints[this.sortedRecoveryPoints.length - 1].timestamp.valueOf()
  }

  private get rpAxisMax(): number {
    return this.enableRealTimeRecovery ? TRACK_MAX - REALTIME_GAP : TRACK_MAX
  }

  get sortedRecoveryPoints() {
    return this.recoveryPoints.sort(rpSortFunction)
  }

  get oldestRpLabel(): string {
    if (!this.recoveryPoints?.length) return ''
    return timeAgoString(this.sortedRecoveryPoints[0].timestamp)
  }

  get newestRpLabel(): string {
    if (this.enableRealTimeRecovery) return 'Now'
    if (!this.recoveryPoints?.length) return ''
    return shortTimeString(this.sortedRecoveryPoints[this.sortedRecoveryPoints.length - 1].timestamp)
  }

  sliderChange() {
    if (!this.recoveryPoints || this.recoveryPoints.length === 0) return
    const rawValue = parseInt(this.rangeControl.nativeElement.value)

    // Track positions past the RP axis are the "Now" zone.
    if (this.enableRealTimeRecovery && rawValue > this.rpAxisMax) {
      this._intent = { kind: 'realTime' }
      this.announceFromIntent()
      return
    }

    // Map the drag position back to a target timestamp, then snap to the
    // nearest actual RP by absolute time distance.
    const ratio = this.rpAxisMax === 0 ? 0 : rawValue / this.rpAxisMax
    const targetTs = this.tMin + ratio * (this.tMax - this.tMin)
    const sorted = this.sortedRecoveryPoints
    let nearest = sorted[0]
    let bestDist = Math.abs(sorted[0].timestamp.valueOf() - targetTs)
    for (let i = 1; i < sorted.length; i++) {
      const d = Math.abs(sorted[i].timestamp.valueOf() - targetTs)
      if (d < bestDist) {
        bestDist = d
        nearest = sorted[i]
      }
    }

    this._intent = { kind: 'rpId', id: nearest.recoveryPointId, anchorTs: nearest.timestamp.valueOf() }
    this.announceFromIntent()
  }

  private announceFromIntent() {
    if (!this.recoveryPoints || this.recoveryPoints.length === 0) return

    let rpDto: RecoveryPointDto | null = null
    let isRealTime = false

    const sorted = this.sortedRecoveryPoints

    if (!this._intent || this._intent.kind === 'latest' || this._intent.kind === 'realTime') {
      // 'latest' / no-intent → newest RP. realTime → emit Now (DTO still set to
      // the newest RP, paired with isRealTime=true).
      if (this._intent?.kind === 'realTime' && this.enableRealTimeRecovery) {
        isRealTime = true
      }
      rpDto = sorted[sorted.length - 1]
    } else if (this._intent.kind === 'rpId') {
      const intentId = this._intent.id
      rpDto = sorted.find((r) => r.recoveryPointId === intentId) ?? null
    } else {
      // 'time' — find newest RP with timestamp ≤ anchor, fall back to oldest.
      const ts = this._intent.ts
      for (let i = sorted.length - 1; i >= 0; i--) {
        if (sorted[i].timestamp.valueOf() <= ts) {
          rpDto = sorted[i]
          break
        }
      }
      if (!rpDto) rpDto = sorted[0]
    }

    if (!rpDto) return

    if (this._lastEmittedRpId === rpDto.recoveryPointId && this._lastEmittedRealTime === isRealTime) return
    this._lastEmittedRpId = rpDto.recoveryPointId
    this._lastEmittedRealTime = isRealTime

    this.update.emit({ recoveryPointDto: rpDto, isRealTime })
  }
}
